import camelCase from 'lodash/camelCase';
import CMS_DATA from '@/lib/data.js';

export type FetchCMSResult<T = unknown> = {
  ok: boolean;
  status: number;
  data?: T | null;
  error?: string | null;
};

const getApiBase = (opts?: { apiBase?: string }) => {
  return opts?.apiBase ?? (
    typeof window === "undefined"
      ? process.env.NEST_API_INTERNAL_BASE
      : process.env.NEXT_PUBLIC_NEST_API_BASE
  ) ?? '';
};

const buildUrl = (apiBase: string, entity: string, populate: string) => {
  let q: string;
  const sep = entity.includes('?') ? '&' : '?';
  if (populate.includes('=')) {
    const [keyPart, ...rest] = populate.split('=');
    const valuePart = rest.join('=');
    const key = `populate${keyPart}`;
    q = `${sep}${encodeURIComponent(key)}=${encodeURIComponent(valuePart)}`;
  } else {
    q = `${sep}populate=${encodeURIComponent(populate)}`;
  }

  return apiBase
    ? `${apiBase}/strapi/${entity}${q}`
    : `/strapi/${entity}${q}`;
};

const parseJsonSafe = async (res: Response) => {
  try {
    return await res.json();
  } catch {
    return null;
  }
};

export const fetchCMS = async <T = unknown>(
  entity: string,
  opts?: {
    populate?: string;
    apiBase?: string;
    normalize?: boolean;
    sort?: string;
    params?: Record<string, string>;
  }
): Promise<FetchCMSResult<T>> => {
  const apiBase = getApiBase(opts);
  const populate = opts?.populate ?? '*';
  let url = buildUrl(apiBase, entity, populate);
  let raw, res: Response = {} as Response;
  let data: T;

  if (opts?.sort) {
    url += `&sort=${opts.sort}`;
  }

  if (opts?.params) {
    for (const [key, value] of Object.entries(opts.params)) {
      url += `&${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    }
  }

  if (process.env.STATIC_BUILD === "false") {
    try {
      res = await fetch(url, {
        next: {
          tags: ['strapi'],
          revalidate: false,
        }
      });
      if (!res.ok) {
        console.log("******* fetch failed: ", res);
        // const bodyText = await readResponseText(res);
        // return { ok: false, status: res.status, data: null, error: bodyText };
      }
  
      const json = await parseJsonSafe(res);
      raw = json?.data ?? null;
      const normalized = (opts?.normalize ? normalizeData(raw) : raw) as unknown;

      data = normalized as unknown as T;
      return { ok: true, status: res?.status, data: data as unknown as T };
    } catch {
      try {
        data = loadFallbackEntity(entity);
      } catch (fallbackErr) {
        return {
          ok: false,
          status: 0,
          data: null,
          error: `CMS failed and fallback failed: ${String(fallbackErr)}`,
        };
      }
    }
  }
  data = loadFallbackEntity(entity);
  return { ok: true, status: res?.status, data: data as unknown as T };
};

const loadFallbackEntity = <T = unknown>(entityType: string): T => {
  return (CMS_DATA as Record<string, unknown>)[camelCase(entityType)] as unknown as T;
};

const normalizeData = (input: unknown): unknown => {
  if (input === null || input === undefined) return input;
  if (Array.isArray(input)) return input.map(normalizeData);
  if (typeof input !== 'object') return input;

  const obj = input as Record<string, unknown>;

  if ('data' in obj && (obj.data !== undefined)) {
    return normalizeData(obj.data);
  }

  if ('attributes' in obj && obj.attributes && typeof obj.attributes === 'object') {
    const { attributes, id, __component, createdAt, updatedAt, locale, publishedAt, ...rest } = obj as Record<string, unknown>;
    const merged = { ...(attributes as Record<string, unknown>), ...rest } as Record<string, unknown>;
    const keys = Object.keys(merged);
    if (keys.length === 1 && 'text' in merged && typeof merged.text === 'string') return merged.text;

    const out: Record<string, unknown> = {};
    for (const k of keys) {
      out[k] = normalizeData(merged[k]);
    }
    if ('description' in merged) {
      out['description'] = richTextToString(merged['description']);
    }
    const meta: Record<string, unknown> = {};
    if (id !== undefined) meta.id = id;
    if (__component !== undefined) meta.__component = __component;
    if (createdAt !== undefined) meta.createdAt = createdAt;
    if (updatedAt !== undefined) meta.updatedAt = updatedAt;
    if (locale !== undefined) meta.locale = locale;
    if (publishedAt !== undefined) meta.publishedAt = publishedAt;
    if (Object.keys(meta).length) out.meta = meta;
    return out;
  }

  const out: Record<string, unknown> = {};
  for (const k of Object.keys(obj)) {
    out[k] = normalizeData(obj[k]);
  }
  const metaKeys = ['id', '__component', 'createdAt', 'updatedAt', 'locale', 'publishedAt'];
  const meta: Record<string, unknown> = {};
  for (const mk of metaKeys) {
    if (mk in out) {
      meta[mk] = out[mk];
      delete out[mk];
    }
  }
  if (Object.keys(meta).length) out.meta = meta;
  // Convert rich text description at root to a plain string
  if ('description' in out && (Array.isArray(out.description) || typeof out.description === 'object')) {
    out.description = richTextToString(out.description as unknown);
  }
  return out;
};

const richTextToString = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value.map(v => richTextToString(v)).filter(Boolean).join('\n\n');
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (Array.isArray(obj.children)) {
      return obj.children.map(c => richTextToString(c)).join('');
    }
    if (typeof obj.text === 'string') return obj.text;
    return Object.values(obj).map(v => richTextToString(v)).filter(Boolean).join(' ');
  }
  return String(value);
};


